<template>
  <DrawerDialog
    :title="internal_comment !== null ? 'Internal Order Comment' : 'Add Internal Order Comment'"
    :showCloseButton="true"
    :showFooter="true"
    :showSaveButton="false"
    :showCancelButton="false"
    :custom-footer="true"
    :sm="true"
    @closeEvent="cancelEvent"
    class="internal-note-modal"
  >
    <template #modal-content>
      <form id="form-add-product" @submit.prevent="onSubmit">
        <!-- Multi-line: Enter is a new line, Cmd/Ctrl+Enter saves. Grows with the
             note between 3 and 10 rows; longer notes scroll. -->
        <textarea
          ref="noteField"
          required
          class="note-field w-full px-3 py-3 my-6 border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Add internal note"
          rows="3"
          data-test="internal-note-field"
          v-model="internalNote"
          @input="autoGrow"
          @keydown.enter.meta.prevent="onSubmit"
          @keydown.enter.ctrl.prevent="onSubmit"
        ></textarea>
        <p class="note-hint">Enter for a new line · Cmd/Ctrl+Enter to save</p>
      </form>
    </template>

    <template #footer>
      <div class="flex justify-end gap-x-3">
        <button
          type="button"
          @click="cancelEvent"
          class="px-4 py-2 bg-surface text-[var(--accent)] rounded-md hover:bg-[var(--accent)] hover:text-white border border-[var(--accent)] transition-colors"
        >
          Close
        </button>
        <button
          @click="deleteInternalNote"
          v-if="internal_comment !== null"
          class="px-4 py-2 bg-surface text-red-700 rounded-md hover:bg-red-700 hover:text-white border border-red-700 transition-colors"
        >
          Delete comment
        </button>
        <button
          type="submit"
          form="form-add-product"
          class="px-4 py-2 bg-[var(--accent)] text-white rounded-md"
          :disabled="isSubmitting"
        >
          Save comment
        </button>
      </div>
    </template>
  </DrawerDialog>
</template>

<script setup lang="ts">
import DrawerDialog from '@/components/Shared/DrawerDialog.vue';
import { messageForError } from '@/utils/userMessage';
import { showToast } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { nextTick, onMounted, ref } from 'vue';

interface InternalComment {
  comment: string;
  /** Absent when the box opens with a draft (AB#21422) rather than a saved note. */
  user_id?: number;
  user_name?: string;
  created_at?: string;
}

interface Props {
  order_item_id: number;
  internal_comment: InternalComment | null;
}

const props = withDefaults(defineProps<Props>(), {
  order_item_id: null,
  internal_comment: null,
});

const emit = defineEmits(['onClose', 'onCancel']);

const isSubmitting = ref<boolean>(false);
const internalNote = ref<string>(props.internal_comment?.comment ?? '');
const noteField = ref<HTMLTextAreaElement | null>(null);

const MIN_ROWS = 3;
const MAX_ROWS = 10;
/** Fit the box to the note, between MIN_ROWS and MAX_ROWS lines. */
const autoGrow = () => {
  const el = noteField.value;
  if (!el) return;
  try {
    const line = parseFloat(getComputedStyle(el).lineHeight) || 22;
    el.style.height = 'auto';
    const rows = Math.min(MAX_ROWS, Math.max(MIN_ROWS, Math.ceil((el.scrollHeight - 24) / line)));
    el.style.height = `${rows * line + 24}px`;
  } catch {
    /* no layout (tests) */
  }
};
onMounted(async () => {
  await nextTick();
  autoGrow();
  try {
    noteField.value?.focus();
  } catch {
    /* no document (tests) */
  }
});
const cancelEvent = () => {
  emit('onCancel');
};
const closeEvent = () => {
  emit('onClose');
};

const onSubmit = async () => {
  const text = internalNote.value.trim();
  if (!text || isSubmitting.value) return;
  const payload = {
    order_item_id: props.order_item_id,
    internal_comment: text,
  };
  try {
    isSubmitting.value = true;
    const response = await http.post(
      `${import.meta.env.VITE_APP_BASE_URL}/add-internal-comment`,
      payload
    );
    if (response.status === 200) {
      showToast('Note added.', 'success');
    }
    closeEvent();
  } catch (e) {
    showToast(messageForError(e, "The note couldn't be added. Try again."), 'error');
    isSubmitting.value = false;
  }
};

const deleteInternalNote = async () => {
  try {
    isSubmitting.value = true;
    const response = await http.delete(
      `${import.meta.env.VITE_APP_BASE_URL}/delete-internal-comment?order_item_id=${props.order_item_id}`
    );
    if (response.status === 200) {
      showToast('Note saved.', 'success');
    }
    closeEvent();
  } catch (e) {
    showToast(messageForError(e, "The note couldn't be saved. Try again."), 'error');
    isSubmitting.value = false;
  }
};
</script>

<style lang="scss" scoped>
.note-field {
  display: block;
  resize: vertical;
  line-height: 22px;
  font: inherit;
  font-size: 14px;
  color: var(--ink);
  background: var(--surface);
}
.note-hint {
  margin: -16px 0 8px;
  font-size: 11.5px;
  color: var(--ink-mute);
}
label {
  &:has(input:checked) {
    @apply bg-primary text-white;

    .checkIcon {
      display: block;
    }
  }
}
</style>
